/**
 * First-class recipe states (#1466 / ADR F-30).
 *
 * Hosts classify transport/domain results and supply localized copy. This
 * component only renders the semantic outcome, keeping empty distinct from
 * failed, offline distinct from refused, and partial distinct from complete.
 * A refused state has no retry field by construction: repeating an act without
 * changed authority is not recovery.
 */
import type { ReactNode } from 'react'
import { EmptyState, ErrorState, Spinner } from '../controls'
import s from './StateBoundary.module.css'

export type ExperienceState =
  | { kind: 'ready' }
  | { kind: 'loading'; label: ReactNode }
  | { kind: 'empty'; message: ReactNode; action?: ReactNode }
  | { kind: 'partial'; title: ReactNode; message?: ReactNode; action?: ReactNode }
  | { kind: 'offline'; title: string; message: string; onRetry?: () => void }
  | { kind: 'error'; title: string; message: string; onRetry?: () => void }
  | { kind: 'refused'; title: string; message: string }

export interface StateBoundaryProps {
  state: ExperienceState
  children?: ReactNode
  testId?: string
}

export function StateBoundary({ state, children, testId }: StateBoundaryProps) {
  if (state.kind === 'ready') return <>{children}</>

  if (state.kind === 'loading') {
    return (
      <div className={s.loading} data-state="loading" data-testid={testId} aria-live="polite">
        <Spinner />
        <span>{state.label}</span>
      </div>
    )
  }

  if (state.kind === 'empty') {
    return (
      <div className={s.state} data-state="empty" data-testid={testId}>
        <EmptyState action={state.action}>{state.message}</EmptyState>
      </div>
    )
  }

  if (state.kind === 'partial') {
    return (
      <div className={s.partial} data-state="partial" data-testid={testId}>
        <section className={s.partialNotice} role="status" aria-label="Partial information">
          <div className={s.partialCopy}>
            <strong>{state.title}</strong>
            {state.message != null && <span>{state.message}</span>}
          </div>
          {state.action != null && <div className={s.action}>{state.action}</div>}
        </section>
        {children}
      </div>
    )
  }

  return (
    <div className={s.state} data-state={state.kind} data-testid={testId}>
      <ErrorState
        title={state.title}
        message={state.message}
        onRetry={state.kind === 'refused' ? undefined : state.onRetry}
      />
    </div>
  )
}
